import {
  Box,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import NoData from "../../components/UI/NoData";
import PagesContainer from "../../components/UI/PagesContainer";
import useFetch from "../../hooks/useFetch";
import { IAppStoreLinks } from "../../models/app-store-links";
import { appStoreLinksGet } from "../../services/appStoreLinks.service";
import { useTranslation } from "react-i18next";
import CompanyLogo from "./CompanyLogo";

const translationPath = "page.appStore.";

// Using constants for app type codes improves readability and maintainability
const APP_TYPE_ANDROID = "2";
const APP_TYPE_IOS = "4";

export const AppStorePage = () => {
  const { t } = useTranslation();
  const { sendRequest } = useFetch(appStoreLinksGet);
  const [links, setLinks] = useState<IAppStoreLinks[]>([]);
  const [loading, setLoading] = useState(true);
  const [redirectingMessage, setRedirectingMessage] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const mobileOS = useMemo(() => {
    const userAgent = window.navigator.userAgent;

    if (/android/i.test(userAgent)) {
      return "android";
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      return "ios";
    }
  }, []);

  const fetchLinks = useCallback(async () => {
    const { data, success } = await sendRequest(undefined);
    if (!success || !data) {
      setLoading(false);
      return;
    }

    // 1. Process and filter links in one go
    const preprocessedData = data.map((row) => ({
      ...row,
      storeLink: row.storeLink
        ? !/^https?:\/\//i.test(row.storeLink)
          ? `https://${row.storeLink}`
          : row.storeLink
        : null,
    }));
    const validLinks = preprocessedData.filter(
      (row): row is IAppStoreLinks & { storeLink: string } => !!row.storeLink
    );

    let redirectUrl: string | null = null;

    if (mobileOS) {
      if (mobileOS === "android") {
        const androidLink = validLinks.find(
          (link) => link.siteAppType.code === APP_TYPE_ANDROID
        );
        if (androidLink) redirectUrl = androidLink.storeLink;
      } else if (mobileOS === "ios") {
        const iosLink = validLinks.find(
          (link) => link.siteAppType.code === APP_TYPE_IOS
        );
        if (iosLink) redirectUrl = iosLink.storeLink;
      }
    } else if (validLinks.length === 1) {
      redirectUrl = validLinks[0].storeLink;
    }

    if (redirectUrl) {
      setRedirectingMessage(
        t(translationPath + "redirecting") || "Redirecting..."
      );
      window.location.href = redirectUrl.trim();
      return;
    }

    const sortedData = preprocessedData.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    setLinks(sortedData);
    setLoading(false);
  }, [sendRequest, t, mobileOS]);

  useLayoutEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  return (
    <PagesContainer
      hideHeader
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>{redirectingMessage}</Typography>
        </Stack>
      ) : (
        <>
          <CompanyLogo />
          {links.length > 0 ? (
            isMobile ? (
              <Stack spacing={2} sx={{ width: "100%", p: 2, mt: 2 }}>
                {links.map((row) => (
                  <Paper
                    key={row.id}
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderRadius: 2,
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "3px",
                        height: "60%",
                        background: theme.palette.primary.main,
                        borderRadius: "0 2px 2px 0",
                      },
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {row.name}
                    </Typography>
                    {row.storeLink && (
                      <Link href={row.storeLink} target="_blank" rel="noopener">
                        {row.siteAppType.code === APP_TYPE_ANDROID ? (
                          <Box
                            component="img"
                            src="/logo-google-play.png"
                            alt="Disponível no Google Play"
                            sx={{ height: 40, display: "block" }}
                          />
                        ) : row.siteAppType.code === APP_TYPE_IOS ? (
                          <Box
                            component="img"
                            src="/logo-app-store.png"
                            alt="Baixar na App Store"
                            sx={{ height: 40, display: "block" }}
                          />
                        ) : null}
                      </Link>
                    )}
                  </Paper>
                ))}
              </Stack>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  width: "100%",
                  maxHeight: 440,
                  borderRadius: 2,
                  boxShadow: theme.shadows[4],
                  overflow: "auto",
                  overflowX: "hidden",
                }}
              >
                <Table
                  sx={{
                    minWidth: 650,
                  }}
                  aria-label="tabela de links"
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        background: theme.palette.primary.main,
                        "& .MuiTableCell-head": {
                          color: theme.palette.primary.contrastText,
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "none",
                          padding: "20px 24px",
                        },
                      }}
                    >
                      <TableCell>
                        {t(translationPath + "name") || "Name"}
                      </TableCell>
                      <TableCell>
                        {t(translationPath + "link") || "Link"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {links.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background: theme.palette.action.hover,
                            transform: "translateX(4px)",
                            boxShadow: theme.shadows[2],
                          },
                          "&:last-child td, &:last-child th": {
                            borderBottom: 0,
                          },
                          "& .MuiTableCell-body": {
                            padding: "16px 24px",
                            fontSize: "0.95rem",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            position: "relative",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: "3px",
                              height: "60%",
                              background: theme.palette.primary.main,
                              borderRadius: "0 2px 2px 0",
                            },
                          }}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            p: 0,
                            border: 0,
                          }}
                        >
                          {row.storeLink && (
                            <Link
                              href={row.storeLink}
                              target="_blank"
                              rel="noopener"
                            >
                              {row.siteAppType.code === APP_TYPE_ANDROID ? (
                                <Box
                                  component="img"
                                  src="/logo-google-play.png"
                                  alt="Disponível no Google Play"
                                  sx={{ height: 40, display: "block" }}
                                />
                              ) : row.siteAppType.code === APP_TYPE_IOS ? (
                                <Box
                                  component="img"
                                  src="/logo-app-store.png"
                                  alt="Baixar na App Store"
                                  sx={{ height: 40, display: "block" }}
                                />
                              ) : null}
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : (
            <NoData
              message={
                t(translationPath + "no_data") || "Nenhum link cadastrado"
              }
            />
          )}
        </>
      )}
    </PagesContainer>
  );
};
